import {
  Brain,
  Calendar,
  ChartBar,
  Chats,
  ClockCountdown,
  ClockCounterClockwise,
  ClockUser,
  Database,
  Gear,
  Lightning,
  Microphone,
  Numpad,
  Phone,
  Plugs,
  Prohibit,
  Repeat,
  Robot,
  SidebarSimple,
  SignOut,
  TextIndent,
  UserCircle,
  Wallet,
  WebhooksLogo,
} from '@phosphor-icons/react';
import { useLocalStorageState } from 'ahooks';
import clsx from 'clsx';
import { getAuth } from 'firebase/auth';
import { forwardRef, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useEnvironment } from 'src/contexts/EnvironmentContext';
import { useNavigation } from 'src/contexts/NavigationContext';
import { useServiceMode } from 'src/contexts/ServiceModeContext';
import { logoutHelper } from 'src/helpers/logoutHelper';
import { breakpoints } from 'src/hooks/useBreakpoint';
import { limitString } from 'src/utils/limitString';
import { Can } from '../hooks/usePermissions';
import {
  EnvironmentSwitcher,
  EnvironmentSwitcherModal,
} from './EnvironmentSwitcher';
import { MenuItem, MenuItemGroup } from './Menu';
import { ServiceModeForm } from './ServiceModeForm';
import { Tooltip } from './Tooltip';
import { StabilityLevel } from 'src/types/stability-level';

const selectedNavClass = 'text-neutral-600 bg-neutral-50';

interface MenuItem {
  subject: string;
  icon: React.ReactNode;
  title: string;
  path: string;
  stabilityLevel?: StabilityLevel;
  children?: MenuItem[];
  childPathPrefix?: string;
}

interface MenuGroup {
  title: string;
  items: MenuItem[];
}

interface SidebarMenuItemProps {
  item: MenuItem;
  isCollapsed: boolean | undefined;
  locationPathname: string;
  navigateWithBlocking: (path: string) => void;
}

const SidebarMenuItem: React.FC<SidebarMenuItemProps> = ({
  item,
  isCollapsed,
  locationPathname,
  navigateWithBlocking,
}) => {
  const hasChildren = !!item.children?.length;
  const childPathPrefix = item.childPathPrefix;

  const isChildActive =
    hasChildren &&
    item.children?.some((child) => locationPathname === child.path);

  const isParentActive = hasChildren
    ? locationPathname === item.path && !isChildActive
    : locationPathname === item.path;

  const showChildren =
    hasChildren &&
    !isCollapsed &&
    !!childPathPrefix &&
    locationPathname.startsWith(childPathPrefix);

  const handleParentClick = () => {
    navigateWithBlocking(item.path);
  };

  const handleChildClick = (childPath: string) => {
    navigateWithBlocking(childPath);
  };

  if (isCollapsed && hasChildren) {
    return (
      <>
        {item.children?.map((child) => (
          <Can
            key={`${item.path}-${child.path}`}
            subject={child.subject}
            operation="read"
          >
            <MenuItem
              icon={child.icon}
              title={child.title}
              collapsedTitle={`${item.title} - ${child.title}`}
              isCollapsed={isCollapsed}
              onClick={() => handleChildClick(child.path)}
              className={clsx(
                locationPathname === child.path ? selectedNavClass : '',
              )}
            />
          </Can>
        ))}
      </>
    );
  }

  return (
    <Can subject={item.subject} operation="read">
      <MenuItem
        icon={item.icon}
        title={item.title}
        isCollapsed={isCollapsed}
        onClick={handleParentClick}
        className={clsx(isParentActive ? selectedNavClass : '')}
        stabilityLevel={item.stabilityLevel}
      >
        {showChildren && (
          <ul className="mt-1 ml-6 space-y-1">
            {item.children?.map((child) => (
              <Can key={child.path} subject={child.subject} operation="read">
                <MenuItem
                  icon={child.icon}
                  title={child.title}
                  isCollapsed={isCollapsed}
                  onClick={() => handleChildClick(child.path)}
                  className={clsx(
                    locationPathname === child.path ? selectedNavClass : '',
                  )}
                />
              </Can>
            ))}
          </ul>
        )}
      </MenuItem>
    </Can>
  );
};

const menuGroups: MenuGroup[] = [
  {
    title: 'Performance',
    items: [
      {
        subject: 'dashboard',
        icon: <ChartBar size={20} />,
        title: 'Dashboard',
        path: '/dashboard',
      },
      {
        subject: 'calls',
        icon: <Phone size={20} />,
        title: 'Calls',
        path: '/calls',
        childPathPrefix: '/calls',
        children: [
          {
            subject: 'calls',
            icon: <ClockCounterClockwise size={20} />,
            title: 'History',
            path: '/calls',
          },
          {
            subject: 'calls',
            icon: <Calendar size={20} />,
            title: 'Scheduled',
            path: '/calls/scheduled',
          },
        ],
      },
    ],
  },
  {
    title: 'Set Up',
    items: [
      {
        subject: 'agents',
        icon: <Robot size={20} />,
        title: 'Agents',
        path: '/agents',
      },
      {
        subject: 'campaigns',
        icon: <ClockUser size={20} />,
        title: 'Campaigns',
        path: '/campaigns',
      },
      {
        subject: 'knowledge-bases',
        icon: <Database size={20} />,
        title: 'Knowledge Bases',
        path: '/knowledge-bases',
        stabilityLevel: StabilityLevel.Beta,
      },
      {
        subject: 'prompts',
        icon: <TextIndent size={20} />,
        title: 'Prompts',
        path: '/prompts',
      },
      {
        subject: 'voices',
        icon: <Microphone size={20} />,
        title: 'Voices',
        path: '/voices',
      },
      {
        subject: 'numbers',
        icon: <Numpad size={20} />,
        title: 'Numbers',
        path: '/numbers',
      },
      {
        subject: 'dnc',
        icon: <Prohibit size={20} />,
        title: 'DNC List',
        path: '/dnc-numbers',
      },
      {
        subject: 'providers',
        icon: <Plugs size={20} />,
        title: 'Providers',
        path: '/providers',
      },
      {
        subject: 'actions',
        icon: <Lightning size={20} />,
        title: 'Actions',
        path: '/actions',
      },
      {
        subject: 'webhooks',
        icon: <WebhooksLogo size={20} />,
        title: 'Webhooks',
        path: '/webhooks',
      },
    ],
  },
];

export const Sidebar = forwardRef<
  HTMLDivElement,
  { className?: string; style?: React.CSSProperties }
>(({ className, style, ...props }, ref) => {
  const [isCollapsed, setIsCollapsed] = useLocalStorageState<boolean>(
    '@control-plane/sidebar-collapsed',
    {
      defaultValue: false,
      listenStorageChange: true,
    },
  );
  const [fireBaseDisplayName] = useLocalStorageState<string | null>(
    '@control-plane/fireBaseDisplayName',
    {
      defaultValue: '',
      listenStorageChange: true,
    },
  );

  const accountMenuItems: MenuItem[] = [
    {
      subject: 'profile',
      icon: <UserCircle size={20} />,
      title: limitString(fireBaseDisplayName || '', 14),
      path: '/profile',
    },
    {
      subject: 'settings',
      icon: <Gear size={20} />,
      title: 'Settings',
      path: '/settings',
    },
    {
      subject: 'billing',
      icon: <Wallet size={20} />,
      title: 'Billing',
      path: '/billing',
    },
    {
      subject: 'logout',
      icon: <SignOut size={20} />,
      title: 'Log out',
      path: '#',
    },
  ];

  const experimentalMenuItems: MenuItem[] = [
    {
      subject: 'calls',
      icon: <Brain size={20} />,
      title: 'Training Center',
      path: '/beta/training-center',
    },
    {
      subject: 'calls',
      icon: <Chats size={20} />,
      title: 'Messages',
      path: '/messages',
    },
    {
      subject: 'providers',
      icon: <Plugs size={20} />,
      title: 'Data Mapping',
      path: '/data-mapping',
    },
  ];

  const auth = getAuth();
  const { environmentData, updateEnvironment } = useEnvironment();
  const { serviceKey } = useServiceMode();
  const location = useLocation();
  const { navigateWithBlocking } = useNavigation();

  const toggleSidebar = () => {
    setIsCollapsed(!isCollapsed);
  };

  useEffect(() => {
    if (!isCollapsed) {
      setHoverLogo(false);
    }

    const handleResize = () => {
      setIsCollapsed(window.innerWidth < breakpoints.tablet);
    };

    const handleResizeLoadIfNotInLocalStorage = () => {
      if (window.innerWidth < breakpoints.tablet && !isCollapsed) {
        setIsCollapsed(true);
      }
    };

    window.addEventListener('resize', handleResize);
    window.addEventListener('load', handleResizeLoadIfNotInLocalStorage);

    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('load', handleResizeLoadIfNotInLocalStorage);
    };
  }, [isCollapsed, setIsCollapsed]);

  const [hoverLogo, setHoverLogo] = useState(false);
  const [modalSwitcherOpen, setModalSwitcherOpen] = useState(false);
  const [useBetaCampaigns] = useLocalStorageState<boolean>(
    '@control-plane/use-beta-campaigns',
    { defaultValue: false, listenStorageChange: true },
  );

  const menuGroupsWithBetaCampaigns = useMemo(() => {
    return menuGroups.map((group) => {
      return {
        ...group,
        items: group.items.map((item) =>
          item.subject === 'campaigns'
            ? {
                ...item,
                path: useBetaCampaigns ? '/beta/campaigns' : '/campaigns',
                stabilityLevel: useBetaCampaigns
                  ? StabilityLevel.Beta
                  : StabilityLevel.Stable,
                childPathPrefix: '/beta/campaigns',
                children: [
                  {
                    subject: 'campaigns',
                    icon: <ClockCountdown size={20} />,
                    title: 'All Campaigns',
                    path: '/beta/campaigns',
                  },
                  {
                    subject: 'campaigns',
                    icon: <Calendar size={20} />,
                    title: 'Schedules',
                    path: '/beta/campaigns/contact-windows',
                  },
                ],
              }
            : item,
        ),
      };
    });
  }, [menuGroups, useBetaCampaigns]);

  return (
    <div ref={ref} className={className} {...props} style={style}>
      <div
        className={clsx(
          'px-5 py-3 bg-neutral-0 text-neutral-0 flex flex-col transition-all duration-300 rounded-l-xl',
          isCollapsed ? 'w-20' : 'w-56',
          serviceKey ? 'bg-warning' : 'bg-neutral-0',
        )}
      >
        <div
          className={clsx(
            'flex items-center py-3',
            isCollapsed
              ? 'justify-center flex-col'
              : 'justify-between flex-row',
          )}
        >
          {isCollapsed ? (
            <Tooltip content="Open Sidebar" position="right">
              <button
                onClick={toggleSidebar}
                className="text-neutral-0 focus:outline-none hover:bg-neutral-50 p-2 rounded-md"
                onMouseEnter={() => setHoverLogo(true)}
                onMouseLeave={() => setHoverLogo(false)}
              >
                {hoverLogo ? (
                  <SidebarSimple className="text-neutral-700" size={24} />
                ) : (
                  <img src="/assets/icon.svg" alt="LOGO" className="w-6 h-6" />
                )}
              </button>
            </Tooltip>
          ) : (
            <img src="/assets/logo.svg" alt="LOGO" className="logo" />
          )}
          {!isCollapsed && (
            <button
              onClick={toggleSidebar}
              className="text-neutral-0 focus:outline-none hover:bg-neutral-50 p-2 rounded-md"
            >
              <SidebarSimple className="text-neutral-700" size={24} />
            </button>
          )}
        </div>
        {!isCollapsed && (
          <ServiceModeForm
            onChange={() => {
              window.location.reload();
            }}
          />
        )}

        {!isCollapsed && (
          <div className="my-5">
            <EnvironmentSwitcher
              onChange={(value) => {
                updateEnvironment({
                  userId: auth.currentUser?.uid || '',
                  envId: value,
                });
                window.location.reload();
              }}
              className="w-20 text-neutral-600"
            />
          </div>
        )}

        {isCollapsed && (
          <>
            <hr className="border-neutral-100 mb-2" />
            <MenuItem
              icon={<Repeat size={20} />}
              title="Switch Environment"
              isCollapsed={isCollapsed}
              onClick={() => setModalSwitcherOpen(true)}
            />
          </>
        )}

        <nav className="flex flex-col justify-between h-full sidebar-nav">
          <ul className="space-y-2">
            {menuGroupsWithBetaCampaigns.map((group) => (
              <MenuItemGroup
                key={group.title}
                title={group.title}
                isCollapsed={isCollapsed}
              >
                {group.items.map((item) => (
                  <SidebarMenuItem
                    key={item.path}
                    item={item}
                    isCollapsed={isCollapsed}
                    locationPathname={location.pathname}
                    navigateWithBlocking={navigateWithBlocking}
                  />
                ))}
              </MenuItemGroup>
            ))}
          </ul>
          {environmentData?.features?.enableExperimentalFeatures && (
            <ul>
              <MenuItemGroup
                title="Experimental Features"
                isCollapsed={isCollapsed}
              >
                {experimentalMenuItems.map((item) => (
                  <SidebarMenuItem
                    key={item.path}
                    item={item}
                    isCollapsed={isCollapsed}
                    locationPathname={location.pathname}
                    navigateWithBlocking={navigateWithBlocking}
                  />
                ))}
              </MenuItemGroup>
            </ul>
          )}
          <ul>
            <MenuItemGroup title="Accounts" isCollapsed={isCollapsed}>
              {accountMenuItems.map((item) =>
                item.subject === 'logout' ? (
                  <MenuItem
                    key={item.subject}
                    icon={item.icon}
                    title={item.title}
                    isCollapsed={isCollapsed}
                    onClick={async () => {
                      return logoutHelper();
                    }}
                  />
                ) : (
                  <Can
                    key={item.subject}
                    subject={item.subject}
                    operation="read"
                  >
                    <MenuItem
                      icon={item.icon}
                      title={item.title}
                      isCollapsed={isCollapsed}
                      onClick={() => navigateWithBlocking(item.path)}
                      className={clsx(
                        'truncate',
                        location.pathname === item.path ? selectedNavClass : '',
                      )}
                    />
                  </Can>
                ),
              )}
            </MenuItemGroup>
          </ul>
        </nav>
      </div>

      <EnvironmentSwitcherModal
        modalOpen={modalSwitcherOpen}
        setModalOpen={setModalSwitcherOpen}
      />
    </div>
  );
});

Sidebar.displayName = 'Sidebar';
